// =====================================================
// Event Management System - TypeScript Types
// Auto-generated from database schema + custom types
// Created: 2026-03-05
// =====================================================

// =====================================================
// Enum Types
// =====================================================

export type EventStatus = 'DRAFT' | 'OPEN' | 'CLOSED' | 'COMPLETED';
export type ParticipantType = 'MINISTER' | 'GUEST';
export type PaymentStatus = 'PENDING' | 'PAID' | 'REVERSED';
export type AttendanceStatus = 'PENDING' | 'ATTENDED' | 'ABSENT';
export type PaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'QR' | 'CARD';

// =====================================================
// Database Row Types (as returned from Supabase)
// =====================================================

export interface Event {
  id: string;
  code: string;
  name: string;
  description: string | null;
  location: string | null;
  start_date: string; // DATE as ISO string
  end_date: string; // DATE as ISO string
  flyer_filename: string | null;
  base_price: number | null;
  agenda?: string | null;
  status: string;
  registration_form_type?: string; // 'SIMPLE' | 'FULL_CONVENTION'
  allow_public_guests?: boolean;
  signature_mode?: string; // 'ON_SITE' | 'DIGITAL'
  registration_deadline?: string | null;
  registration_declaration?: string | null;
  payment_instructions?: string | null;
  payment_qr_filename?: string | null; // Returned from database as string, cast to EventStatus when needed
  published_at: string | null; // TIMESTAMP as ISO string
  closed_at: string | null;
  completed_at: string | null;
  created_by: string | null; // UUID
  updated_by: string | null; // UUID
  created_at: string | null; // TIMESTAMP as ISO string
  updated_at: string | null; // TIMESTAMP as ISO string
}

export interface EventSession {
  id: string;
  event_id: string; // UUID FK to events
  name: string;
  description: string | null;
  session_date: string; // DATE as ISO string
  start_time: string | null; // TIME as string "HH:MM:SS"
  end_time: string | null;
  display_order: number | null;
  created_by: string | null; // UUID
  updated_by: string | null; // UUID
  created_at: string | null; // TIMESTAMP as ISO string
  updated_at: string | null; // TIMESTAMP as ISO string
}

export interface EventGuest {
  id: string;
  full_name: string;
  dni: string | null;
  email: string | null;
  phone: string | null;
  age: number | null;
  church_affiliation: string | null;
  representing_minister_id: string | null; // UUID FK to ministers
  created_by: string; // UUID
  updated_by: string | null; // UUID
  created_at: string; // TIMESTAMP as ISO string
  updated_at: string; // TIMESTAMP as ISO string
}

export interface EventParticipant {
  id: string;
  event_id: string; // UUID FK to events
  minister_id: string | null; // UUID FK to ministers
  guest_id: string | null; // UUID FK to event_guests
  participant_type: ParticipantType;
  is_mandatory: boolean;
  registered_at: string; // TIMESTAMP as ISO string
  registered_by: string; // UUID
  calculated_price: number;
  applied_rule_id: string | null; // Will be FK in Stage 2
  payment_status: PaymentStatus;
  payment_amount: number | null;
  paid_at: string | null; // TIMESTAMP as ISO string
  paid_by: string | null; // UUID
  attendance_status: AttendanceStatus;
  checked_in_at: string | null; // TIMESTAMP as ISO string
  checked_in_by: string | null; // UUID
  notes: string | null;
  updated_by: string | null; // UUID
}

// =====================================================
// Insert Types (for creating new records)
// =====================================================

export interface EventInsert {
  id?: string;
  code: string;
  name: string;
  description?: string | null;
  location?: string | null;
  start_date: string;
  end_date: string;
  flyer_filename?: string | null;
  base_price?: number | null;
  agenda?: string | null;
  status?: string; // Database accepts string
  published_at?: string | null;
  closed_at?: string | null;
  completed_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface EventSessionInsert {
  id?: string;
  event_id: string;
  name: string;
  description?: string | null;
  session_date: string;
  start_time?: string | null;
  end_time?: string | null;
  display_order?: number | null;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface EventGuestInsert {
  id?: string;
  full_name: string;
  dni?: string | null;
  email?: string | null;
  phone?: string | null;
  age?: number | null;
  church_affiliation?: string | null;
  representing_minister_id?: string | null;
  created_by: string;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface EventParticipantInsert {
  id?: string;
  event_id: string;
  minister_id?: string | null;
  guest_id?: string | null;
  participant_type: ParticipantType;
  is_mandatory?: boolean;
  registered_at?: string;
  registered_by: string;
  calculated_price: number;
  applied_rule_id?: string | null;
  payment_status?: PaymentStatus;
  payment_amount?: number | null;
  paid_at?: string | null;
  paid_by?: string | null;
  attendance_status?: AttendanceStatus;
  checked_in_at?: string | null;
  checked_in_by?: string | null;
  notes?: string | null;
  updated_by?: string | null;
}

// =====================================================
// Update Types (for updating existing records)
// =====================================================

export interface EventUpdate {
  code?: string;
  name?: string;
  description?: string | null;
  location?: string | null;
  start_date?: string;
  end_date?: string;
  flyer_filename?: string | null;
  base_price?: number | null;
  agenda?: string | null;
  status?: string; // Database accepts string
  published_at?: string | null;
  closed_at?: string | null;
  completed_at?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface EventSessionUpdate {
  name?: string;
  description?: string | null;
  session_date?: string;
  start_time?: string | null;
  end_time?: string | null;
  display_order?: number | null;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface EventGuestUpdate {
  full_name?: string;
  dni?: string | null;
  email?: string | null;
  phone?: string | null;
  age?: number | null;
  church_affiliation?: string | null;
  representing_minister_id?: string | null;
  updated_by?: string | null;
  updated_at?: string;
}

export interface EventParticipantUpdate {
  is_mandatory?: boolean;
  calculated_price?: number;
  applied_rule_id?: string | null;
  payment_status?: PaymentStatus;
  payment_amount?: number | null;
  paid_at?: string | null;
  paid_by?: string | null;
  attendance_status?: AttendanceStatus;
  checked_in_at?: string | null;
  checked_in_by?: string | null;
  notes?: string | null;
  updated_by?: string | null;
}

// =====================================================
// Extended Types (with joined data)
// =====================================================

// Import existing types from Supabase schema
import type { Database } from './supabase';

export type Minister = Database['public']['Tables']['ministers']['Row'];
export type User = Database['public']['Tables']['users']['Row'];

export interface EventWithSessions extends Event {
  sessions?: EventSession[];
}

export interface EventWithStatistics extends Event {
  statistics?: EventStatistics;
}

export interface EventParticipantWithDetails extends EventParticipant {
  minister?: Minister;
  guest?: EventGuest;
  representing_minister?: Minister; // If guest represents a minister
  registered_by_user?: User;
  paid_by_user?: User;
  checked_in_by_user?: User;
  updated_by_user?: User;
}

export interface EventGuestWithRepresenting extends EventGuest {
  representing_minister?: Minister;
}

// =====================================================
// Statistics & Aggregation Types
// =====================================================

export interface EventStatistics {
  total_registered: number;
  total_ministers: number;
  total_guests: number;
  total_mandatory: number;
  total_paid: number;
  total_pending: number;
  total_attended: number;
  revenue_registrations: number;
}

export interface EventFilters {
  status?: EventStatus;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface ParticipantFilters {
  type?: ParticipantType;
  paymentStatus?: PaymentStatus;
  attendanceStatus?: AttendanceStatus;
  search?: string;
}

// =====================================================
// Form Types (for UI components)
// =====================================================

export interface EventFormData {
  code: string;
  name: string;
  description?: string;
  location?: string;
  start_date: Date | string;
  end_date: Date | string;
  base_price: number;
  agenda?: string;
  flyer?: File | null;
}

export interface EventSessionFormData {
  name: string;
  description?: string;
  session_date: Date | string;
  start_time?: string;
  end_time?: string;
}

export interface EventGuestFormData {
  full_name: string;
  dni?: string;
  email?: string;
  phone?: string;
  age?: number;
  church_affiliation?: string;
  representing_minister_id?: string;
}

export interface PaymentRecordFormData {
  participant_id: string;
  amount: number;
  payment_method: PaymentMethod;
  reference_number?: string;
  payment_date: Date | string;
}

// =====================================================
// Action & Response Types
// =====================================================

export interface EventStatusTransition {
  event_id: string;
  new_status: EventStatus;
  transitioned_by: string;
  transitioned_at: string;
}

export interface ParticipantCheckIn {
  participant_id: string;
  checked_in_by: string;
  checked_in_at: string;
}

export interface PaymentRecord {
  participant_id: string;
  amount: number;
  payment_method: PaymentMethod;
  reference_number?: string;
  payment_date: string;
  paid_by: string;
}

// =====================================================
// API Response Types
// =====================================================

export interface EventListResponse {
  events: Event[];
  total: number;
  page: number;
  per_page: number;
}

export interface ParticipantListResponse {
  participants: EventParticipantWithDetails[];
  total: number;
  page: number;
  per_page: number;
}

// =====================================================
// Helper Types
// =====================================================

export type EventWithRelations = Event & {
  sessions?: EventSession[];
  participants?: EventParticipantWithDetails[];
  created_by_user?: User;
  updated_by_user?: User;
};

// For pagination
export interface PaginationParams {
  page?: number;
  per_page?: number;
}

// For sorting
export interface SortParams {
  column: string;
  direction: 'asc' | 'desc';
}

// Combined query params
export interface EventQuery
  extends EventFilters, PaginationParams, SortParams {}
export interface ParticipantQuery
  extends ParticipantFilters, PaginationParams, SortParams {}

// =====================================================
// Constants
// =====================================================

export const EVENT_STATUSES: EventStatus[] = [
  'DRAFT',
  'OPEN',
  'CLOSED',
  'COMPLETED',
];
export const PARTICIPANT_TYPES: ParticipantType[] = ['MINISTER', 'GUEST'];
export const PAYMENT_STATUSES: PaymentStatus[] = [
  'PENDING',
  'PAID',
  'REVERSED',
];
export const ATTENDANCE_STATUSES: AttendanceStatus[] = [
  'PENDING',
  'ATTENDED',
  'ABSENT',
];
export const PAYMENT_METHODS: PaymentMethod[] = [
  'CASH',
  'BANK_TRANSFER',
  'QR',
  'CARD',
];

// Status labels (Spanish)
export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  DRAFT: 'Borrador',
  OPEN: 'Inscripciones Abiertas',
  CLOSED: 'Inscripciones Cerradas',
  COMPLETED: 'Completado',
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  PENDING: 'Pendiente',
  PAID: 'Pagado',
  REVERSED: 'Anulado',
};

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  PENDING: 'Pendiente',
  ATTENDED: 'Asistió',
  ABSENT: 'Ausente',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CASH: 'Efectivo',
  BANK_TRANSFER: 'Transferencia Bancaria',
  QR: 'Código QR',
  CARD: 'Tarjeta',
};
